import { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { fetchCoaching, clearCoaching, updateCoaching } from '../store/slices/coachingsSlice';
import { toast } from 'react-toastify';
import api from '../services/api';
import {
  ArrowLeft, Pencil, RefreshCw, AlertTriangle,
  User, Calendar, CreditCard, FileText,
  CheckCircle, Clock, XCircle, RotateCcw,
  Upload, Eye, Paperclip, Printer,
  BookOpen, Plus, Trash2,
} from 'lucide-react';

const TYPE_DOC_LABELS = {
  cni: "CNI", passeport: "Passeport", fiche_id: "Fiche d'identification",
  contrat: "Contrat signé", avenant: "Avenant", autre: "Autre",
};

const SGI_LABELS = {
  sgi_bs:                 'SGI BS',
  sgi_pcm:                'SGI PCM',
  nsia_finance:           'NSIA FINANCE',
  sgi_bici_bourse:        'SGI BICI BOURSE',
  societe_generale:       'SOCIÉTÉ GÉNÉRALE',
  atlantique_finance:     'ATLANTIQUE FINANCE',
  boa_capital_securities: 'BOA CAPITAL SECURITIES',
  autre:                  'Autre',
};

const PAIEMENT_BADGE = { paye: 'vert', partiel: 'orange', impaye: 'rouge' };

/* ─────────────── constantes ─────────────── */
const DOMAINES = {
  action:     'ACTION',
  obligation: 'OBLIGATION',
  autre:      'AUTRE',
};

const STATUT_MAP = {
  en_cours: { label: 'En cours', badge: 'vert',   Icon: Clock       },
  expire:   { label: 'Expiré',   badge: 'gris',   Icon: CheckCircle },
  resilie:  { label: 'Résilié',  badge: 'rouge',  Icon: XCircle     },
};

const PAIEMENT_MAP = {
  paye:    { label: 'Payé',    badge: 'vert'   },
  partiel: { label: 'Partiel', badge: 'orange' },
  impaye:  { label: 'Impayé',  badge: 'rouge'  },
};

/* ─────────────── helpers ─────────────── */
const fmt = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '—';
const num = (n) => parseInt(n || 0).toLocaleString('fr-FR');

const S = {
  card:   { padding:'10px 14px', display:'flex', flexDirection:'column' },
  head:   { display:'flex', alignItems:'center', gap:7, paddingBottom:8,
            borderBottom:'1px solid #E5E7EB', marginBottom:10 },
  htitle: { fontSize:11, fontWeight:700, color:'#1F4E79', margin:0,
            textTransform:'uppercase', letterSpacing:'0.05em' },
  grid2:  { display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px 14px' },
};

function SHead({ icon: Icon, title, color = '#1F4E79', right }) {
  return (
    <div style={{ ...S.head, justifyContent:'space-between' }}>
      <div style={{ display:'flex', alignItems:'center', gap:7 }}>
        <Icon size={13} color={color}/>
        <span style={{ ...S.htitle, color }}>{title}</span>
      </div>
      {right}
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div style={{ fontSize:10, fontWeight:600, color:'#9CA3AF', textTransform:'uppercase', letterSpacing:'0.04em' }}>{label}</div>
      <div style={{ fontSize:12, fontWeight:600, color:'#1F2937', marginTop:2 }}>{value ?? '—'}</div>
    </div>
  );
}

/* ─────────────── modal renouvellement ─────────────── */
function ModalRenouveler({ coaching, onClose }) {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    nouvelle_duree_mois: '',
    nouveau_prix:        String(coaching?.prix || ''),
    montant_paye:        '0',
    statut_paiement:     'impaye',
    observation:         '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [dateFinCalc, setDateFinCalc] = useState('');

  useEffect(() => {
    if (coaching?.date_fin && form.nouvelle_duree_mois > 0) {
      const d = new Date(coaching.date_fin);
      d.setMonth(d.getMonth() + parseInt(form.nouvelle_duree_mois));
      setDateFinCalc(d.toLocaleDateString('fr-FR'));
    } else {
      setDateFinCalc('');
    }
  }, [coaching?.date_fin, form.nouvelle_duree_mois]);

  const handleChange = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nouvelle_duree_mois || parseInt(form.nouvelle_duree_mois) < 1) {
      toast.error('La durée est obligatoire (min. 1 mois).');
      return;
    }
    if (!form.nouveau_prix || parseInt(form.nouveau_prix) < 1) {
      toast.error('Le nouveau prix est obligatoire.');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/coachings/${coaching.id}/renouveler/`, {
        nouvelle_duree_mois: parseInt(form.nouvelle_duree_mois),
        nouveau_prix:        parseInt(form.nouveau_prix),
        montant_paye:        parseInt(form.montant_paye) || 0,
        statut_paiement:     form.statut_paiement,
        observation:         form.observation,
      });
      toast.success('Contrat renouvelé — nouveau contrat créé.');
      onClose();
      navigate(`/coachings/${data.nouveau_coaching_id}`);
    } catch (err) {
      const msg = err.response?.data?.error || 'Erreur lors du renouvellement.';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
      zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center',
    }} onClick={onClose}>
      <div style={{
        background: '#fff', borderRadius: 12, padding: 28, width: '100%', maxWidth: 520,
        boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
      }} onClick={e => e.stopPropagation()}>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <RefreshCw size={20} color="#1F4E79" />
          <h2 style={{ fontSize: 17, fontWeight: 700, color: '#1F4E79', margin: 0 }}>
            Renouveler le contrat de coaching
          </h2>
        </div>

        {/* Récap ancien contrat */}
        <div style={{ background: '#EBF3FB', borderRadius: 8, padding: '10px 14px', marginBottom: 18, fontSize: 13, color: '#1F4E79' }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>Contrat actuel #{coaching?.id} — {coaching?.client_nom}</div>
          <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', fontSize: 12 }}>
            <span><strong>Fin :</strong> {fmt(coaching?.date_fin)}</span>
            <span><strong>Prix :</strong> {num(coaching?.prix)} FCFA</span>
            <span><strong>Produits :</strong> {DOMAINES[coaching?.domaine] || coaching?.domaine}</span>
          </div>
          <div style={{ marginTop: 6, fontSize: 11, color: '#C00000', fontWeight: 600 }}>
            Ce contrat passera en « Expiré » — un nouveau contrat sera créé automatiquement.
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>

            <div className="form-group">
              <label className="form-label">Nouvelle durée (mois) <span style={{ color: '#C00000' }}>*</span></label>
              <input type="number" min="1" className="form-control" name="nouvelle_duree_mois"
                value={form.nouvelle_duree_mois} onChange={handleChange} placeholder="Ex : 3" />
            </div>

            <div className="form-group">
              <label className="form-label">Nouveau prix (FCFA) <span style={{ color: '#C00000' }}>*</span></label>
              <input type="number" min="1" className="form-control" name="nouveau_prix"
                value={form.nouveau_prix} onChange={handleChange} />
            </div>

            <div className="form-group">
              <label className="form-label">Montant déjà payé (FCFA)</label>
              <input type="number" min="0" className="form-control" name="montant_paye"
                value={form.montant_paye} onChange={handleChange} />
            </div>

            <div className="form-group">
              <label className="form-label">Statut paiement</label>
              <select className="form-control" name="statut_paiement" value={form.statut_paiement} onChange={handleChange}>
                <option value="impaye">Impayé</option>
                <option value="partiel">Partiel</option>
                <option value="paye">Payé</option>
              </select>
            </div>

          </div>

          {dateFinCalc && (
            <div style={{ background: '#E8F5E9', border: '1px solid #A5D6A7', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 13, color: '#1E7145', fontWeight: 600 }}>
              Nouveau contrat : du {fmt(coaching?.date_fin)} au {dateFinCalc}
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Observations / Conditions</label>
            <textarea className="form-control" rows={3} name="observation"
              value={form.observation} onChange={handleChange}
              placeholder="Conditions du renouvellement, avenant, etc." style={{ resize: 'vertical' }} />
          </div>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
            <button type="button" className="header-btn btn-secondary" onClick={onClose}
              style={{ padding: '9px 20px', fontSize: 14 }}>
              Annuler
            </button>
            <button type="submit" className="header-btn btn-success" disabled={submitting}
              style={{ padding: '9px 24px', fontSize: 14, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
              <RefreshCw size={15} />
              {submitting ? 'Création...' : 'Créer le nouveau contrat'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ─────────────── page principale ─────────────── */
export default function CoachingDetailPage() {
  const { id }   = useParams();
  const dispatch = useDispatch();
  const navigate        = useNavigate();

  const { coaching, loadingOne, errorOne } = useSelector(s => s.coachings);
  const [showModal, setShowModal] = useState(false);
  const [changingStatut, setChangingStatut] = useState(false);

  useEffect(() => {
    dispatch(fetchCoaching(id));
    return () => { dispatch(clearCoaching()); };
  }, [dispatch, id]);

  const handleChangerStatut = async (nouveauStatut) => {
    if (!window.confirm(`Passer le coaching au statut "${STATUT_MAP[nouveauStatut]?.label}" ?`)) return;
    setChangingStatut(true);
    try {
      await dispatch(updateCoaching({ id, payload: { statut: nouveauStatut } })).unwrap();
      toast.success('Statut mis à jour.');
      dispatch(fetchCoaching(id));
    } catch {
      toast.error('Erreur lors de la mise à jour du statut.');
    } finally {
      setChangingStatut(false);
    }
  };

  /* ── Chargement / Erreur ── */
  if (loadingOne) {
    return (
      <div className="spinner" style={{ marginTop: 60 }}>
        <div className="spinner-ring" /> Chargement du coaching...
      </div>
    );
  }

  if (errorOne || !coaching) {
    return (
      <div style={{ marginTop: 60, textAlign: 'center' }}>
        <AlertTriangle size={40} color="#C00000" style={{ marginBottom: 12 }} />
        <p style={{ fontSize: 16, color: '#C00000', fontWeight: 600 }}>
          Coaching introuvable ou erreur de chargement.
        </p>
        <button className="header-btn btn-secondary" onClick={() => navigate('/coachings')}
          style={{ marginTop: 12, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <ArrowLeft size={14} /> Retour à la liste
        </button>
      </div>
    );
  }

  const statut   = STATUT_MAP[coaching.statut]   || { label: coaching.statut,           badge: 'gris', Icon: Clock };
  const paiement = PAIEMENT_MAP[coaching.statut_paiement] || { label: coaching.statut_paiement, badge: 'gris' };
  const pct      = coaching.prix && coaching.prix !== '0'
    ? Math.min(100, Math.round((parseFloat(coaching.montant_paye) / parseFloat(coaching.prix)) * 100))
    : 0;

  const jours = coaching.jours_restants;
  const urgenceColor = jours === null ? null : jours < 0 ? '#C00000' : jours <= 15 ? '#C00000' : jours <= 60 ? '#ED7D31' : '#1E7145';

  const peutRenouveler = coaching.statut === 'en_cours';
  const peutExpirer    = coaching.statut === 'en_cours';
  const peutResilier   = coaching.statut === 'en_cours';

  return (
    <div style={{ height:'100%', display:'flex', flexDirection:'column', overflow:'hidden' }}>

      {/* ── En-tête impression ── */}
      <div className="print-header">
        <div className="print-header-logo">CRM-NANCOZI — Fiche Coaching #{coaching.id}</div>
        <div className="print-header-date">Imprimé le {new Date().toLocaleDateString('fr-FR')}</div>
      </div>

      {/* ── Barre d'en-tête ── */}
      <div style={{ flexShrink:0, display:'flex', alignItems:'center', justifyContent:'space-between',
        paddingBottom:10, marginBottom:10, borderBottom:'1px solid #E5E7EB', gap:10, flexWrap:'wrap' }}>

        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <button className="header-btn btn-secondary btn-sm no-print"
            onClick={() => navigate('/coachings')}
            style={{ display:'flex', alignItems:'center', gap:5 }}>
            <ArrowLeft size={13}/> Retour
          </button>
          <div>
            <div style={{ display:'flex', alignItems:'center', gap:7, flexWrap:'wrap' }}>
              <span style={{ fontSize:15, fontWeight:700, color:'#1F2937' }}>Coaching — {coaching.client_nom}</span>
              <span className={`badge badge-${statut.badge}`}>{statut.label}</span>
              <span className={`badge badge-${paiement.badge}`}>{paiement.label}</span>
              {coaching.numero_renouvellement > 0
                ? <span style={{ fontSize:10, fontWeight:700, padding:'2px 7px', borderRadius:5, background:'#EDE9FE', color:'#5B21B6' }}>
                    Renouvellement #{coaching.numero_renouvellement}
                  </span>
                : <span style={{ fontSize:10, fontWeight:600, padding:'2px 7px', borderRadius:5, background:'#E8F5E9', color:'#1E7145' }}>
                    1er contrat
                  </span>
              }
              {jours !== null && jours !== undefined && (
                <span style={{ fontSize:11, color:urgenceColor, fontWeight:600 }}>
                  {jours < 0 ? `Échu ${Math.abs(jours)} j` : `J-${jours}`}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="no-print" style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
          <button className="header-btn btn-secondary btn-sm" onClick={() => window.print()}
            style={{ display:'flex', alignItems:'center', gap:5 }}>
            <Printer size={13}/> PDF
          </button>
          <button className="header-btn btn-secondary btn-sm"
            onClick={() => navigate(`/coachings/${id}/modifier`)}
            style={{ display:'flex', alignItems:'center', gap:5 }}>
            <Pencil size={13}/> Modifier
          </button>
          {peutRenouveler && (
            <button className="header-btn btn-success btn-sm" onClick={() => setShowModal(true)}
              style={{ display:'flex', alignItems:'center', gap:5 }}>
              <RefreshCw size={13}/> Renouveler
            </button>
          )}
          {peutExpirer && (
            <button className="header-btn btn-secondary btn-sm"
              onClick={() => handleChangerStatut('expire')} disabled={changingStatut}
              style={{ display:'flex', alignItems:'center', gap:5 }}>
              <CheckCircle size={13}/> Expiré
            </button>
          )}
          {peutResilier && (
            <button className="header-btn btn-danger btn-sm"
              onClick={() => handleChangerStatut('resilie')} disabled={changingStatut}
              style={{ display:'flex', alignItems:'center', gap:5 }}>
              <XCircle size={13}/> Résilier
            </button>
          )}
        </div>
      </div>

      {/* ── Alerte critique ── */}
      {coaching.est_critique && (
        <div style={{ flexShrink:0, display:'flex', alignItems:'center', gap:8,
          background:'#FCE4EC', border:'1px solid #EF9A9A', borderRadius:8,
          padding:'8px 14px', marginBottom:10, color:'#C00000', fontWeight:600, fontSize:12 }}>
          <AlertTriangle size={14}/>
          Échéance critique : expire dans moins de 15 jours ({fmt(coaching.date_fin)})
        </div>
      )}

      {/* ── Contenu scrollable ── */}
      <div style={{ flex:1, minHeight:0, overflowY:'auto', display:'flex', flexDirection:'column', gap:12 }}>

        {/* KPIs */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:10 }}>
          {[
            { label:'Prix total',        value:`${num(coaching.prix)} FCFA`,         color:'#1F4E79', bg:'#EBF3FB' },
            { label:'Montant payé',      value:`${num(coaching.montant_paye)} FCFA`,  color:'#1E7145', bg:'#E8F5E9' },
            { label:'Solde restant',     value:`${num(coaching.solde_restant)} FCFA`, color:coaching.solde_restant>0?'#C00000':'#1E7145', bg:coaching.solde_restant>0?'#FCE4EC':'#E8F5E9' },
            { label:'Durée',             value:`${coaching.duree_mois} mois`,         color:'#6B7280', bg:'#F9FAFB' },
            { label:'Séances réalisées', value:`${coaching.nb_seances?.realisees??0} / ${coaching.nb_seances?.total??0}`, color:'#7C3AED', bg:'#F5F3FF' },
          ].map(k => (
            <div key={k.label} className="card" style={{ padding:'8px 12px', borderLeft:`3px solid ${k.color}`, background:k.bg }}>
              <div style={{ fontSize:9, color:'#6B7280', fontWeight:600, textTransform:'uppercase', marginBottom:3 }}>{k.label}</div>
              <div style={{ fontSize:15, fontWeight:700, color:k.color }}>{k.value}</div>
            </div>
          ))}
        </div>

        {/* Grille principale 3fr | 2fr */}
        <div style={{ display:'grid', gridTemplateColumns:'3fr 2fr', gap:12, alignItems:'start' }}>

          {/* ── Col gauche ── */}
          <div style={{ display:'flex', flexDirection:'column', gap:12 }}>

            {/* Contrat */}
            <div className="card" style={S.card}>
              <SHead icon={FileText} title="Informations contrat"/>
              <div style={S.grid2}>
                <Field label="Type" value={coaching.type_coaching === 'individuel' ? 'Individuel' : 'Groupe'}/>
                <Field label="Produits" value={DOMAINES[coaching.domaine] || coaching.domaine}/>
                <Field label="SGI" value={coaching.sgi ? (SGI_LABELS[coaching.sgi] || coaching.sgi) : '—'}/>
                <Field label="Statut contrat" value={statut.label}/>
                <Field label="Statut paiement" value={paiement.label}/>
                <Field label="Commercial" value={coaching.commercial_nom}/>
              </div>
            </div>

            {/* Dates */}
            <div className="card" style={S.card}>
              <SHead icon={Calendar} title="Dates"/>
              <div style={S.grid2}>
                <Field label="Date de début" value={fmt(coaching.date_debut)}/>
                <Field label="Date de fin" value={fmt(coaching.date_fin)}/>
                <Field label="Durée totale" value={`${coaching.duree_mois} mois`}/>
                <Field label="Jours restants" value={
                  jours === null ? '—' :
                  jours < 0
                    ? <span style={{ color:'#C00000', fontWeight:700 }}>Échu ({Math.abs(jours)} j)</span>
                    : <span style={{ color:urgenceColor, fontWeight:700 }}>{jours} j</span>
                }/>
                <Field label="Créé le" value={fmt(coaching.created_at)}/>
              </div>
            </div>

            {/* Paiement */}
            <div className="card" style={S.card}>
              <SHead icon={CreditCard} title="Paiement"/>
              <div style={{ marginBottom:10 }}>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
                  <span style={{ fontSize:10, color:'#6B7280', fontWeight:600 }}>Avancement du paiement</span>
                  <span style={{ fontSize:12, fontWeight:700, color:pct>=100?'#1E7145':pct>=50?'#ED7D31':'#C00000' }}>{pct}%</span>
                </div>
                <div style={{ height:7, background:'#E5E7EB', borderRadius:4 }}>
                  <div style={{ width:`${pct}%`, height:'100%', borderRadius:4, transition:'width 0.4s',
                    background:pct>=100?'#1E7145':pct>=50?'#ED7D31':'#C00000' }}/>
                </div>
              </div>
              <div style={S.grid2}>
                <Field label="Prix convenu" value={`${num(coaching.prix)} FCFA`}/>
                <Field label="Montant payé" value={`${num(coaching.montant_paye)} FCFA`}/>
                <Field label="Solde restant" value={`${num(coaching.solde_restant)} FCFA`}/>
                <Field label="Taux de paiement" value={`${pct}%`}/>
              </div>
            </div>

            {/* Observations */}
            {coaching.observation && (
              <div className="card" style={S.card}>
                <SHead icon={FileText} title="Observations"/>
                <p style={{ fontSize:12, color:'#1F2937', whiteSpace:'pre-wrap', margin:0, lineHeight:1.6 }}>
                  {coaching.observation}
                </p>
              </div>
            )}
          </div>

          {/* ── Col droite ── */}
          <div style={{ display:'flex', flexDirection:'column', gap:12 }}>

            {/* Client */}
            <div className="card" style={S.card}>
              <SHead icon={User} title="Client"/>
              <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                <Field label="Nom" value={coaching.client_nom}/>
                <Field label="Commercial" value={coaching.commercial_nom}/>
              </div>
            </div>

            {/* Séances */}
            <SeancesCoaching coaching={coaching} onRefresh={() => dispatch(fetchCoaching(id))}/>

            {/* Documents */}
            <DocumentsCoaching clientId={coaching.client} coachingId={coaching.id}/>
          </div>
        </div>
      </div>

      {/* Modal renouvellement */}
      {showModal && (
        <ModalRenouveler coaching={coaching} onClose={() => setShowModal(false)}/>
      )}
    </div>
  );
}

/* ─────────────── Séances ─────────────── */
const STATUT_SEANCE_MAP = {
  realisee:  { label: 'Réalisée',  badge: 'vert',   color: '#1E7145' },
  annulee:   { label: 'Annulée',   badge: 'rouge',  color: '#C00000' },
  reportee:  { label: 'Reportée',  badge: 'orange', color: '#ED7D31' },
};

function ModalAjouterSeance({ coachingId, onClose, onSuccess }) {
  const [form, setForm] = useState({
    date_seance:   new Date().toISOString().slice(0, 10),
    duree_minutes: '60',
    theme:         '',
    compte_rendu:  '',
    statut:        'realisee',
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.date_seance) { toast.error('La date de la séance est obligatoire.'); return; }
    setSubmitting(true);
    try {
      await api.post(`/coachings/${coachingId}/seances/`, {
        date_seance:   form.date_seance,
        duree_minutes: parseInt(form.duree_minutes) || 60,
        theme:         form.theme,
        compte_rendu:  form.compte_rendu,
        statut:        form.statut,
      });
      toast.success('Séance enregistrée avec succès.');
      onSuccess();
    } catch (err) {
      const msg = err.response?.data
        ? Object.values(err.response.data).flat().join(' | ')
        : 'Erreur lors de l\'enregistrement.';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
      zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center',
    }} onClick={onClose}>
      <div style={{
        background: '#fff', borderRadius: 12, padding: 28, width: '100%', maxWidth: 540,
        boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
      }} onClick={e => e.stopPropagation()}>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
          <BookOpen size={20} color="#7C3AED" />
          <h2 style={{ fontSize: 17, fontWeight: 700, color: '#7C3AED', margin: 0 }}>
            Enregistrer une séance
          </h2>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>

            <div className="form-group">
              <label className="form-label">Date de la séance <span style={{ color: '#C00000' }}>*</span></label>
              <input type="date" className="form-control" name="date_seance"
                value={form.date_seance} onChange={handleChange} />
            </div>

            <div className="form-group">
              <label className="form-label">Durée (minutes)</label>
              <input type="number" min="1" className="form-control" name="duree_minutes"
                value={form.duree_minutes} onChange={handleChange} placeholder="60" />
            </div>

            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label className="form-label">Thème / Objectif de la séance</label>
              <input type="text" className="form-control" name="theme"
                value={form.theme} onChange={handleChange}
                placeholder="Ex : Analyse de portefeuille, Stratégie d'investissement…" />
            </div>

            <div className="form-group">
              <label className="form-label">Statut</label>
              <select className="form-control" name="statut" value={form.statut} onChange={handleChange}>
                <option value="realisee">Réalisée</option>
                <option value="reportee">Reportée</option>
                <option value="annulee">Annulée</option>
              </select>
            </div>

          </div>

          <div className="form-group">
            <label className="form-label">Compte rendu / Notes de suivi</label>
            <textarea className="form-control" rows={4} name="compte_rendu"
              value={form.compte_rendu} onChange={handleChange}
              placeholder="Points abordés, décisions prises, actions à suivre pour la prochaine séance…"
              style={{ resize: 'vertical' }} />
          </div>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
            <button type="button" className="header-btn btn-secondary" onClick={onClose}
              style={{ padding: '9px 20px', fontSize: 14 }}>
              Annuler
            </button>
            <button type="submit" className="header-btn btn-primary" disabled={submitting}
              style={{ padding: '9px 24px', fontSize: 14, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8, background: '#7C3AED', borderColor: '#7C3AED' }}>
              <BookOpen size={15} />
              {submitting ? 'Enregistrement…' : 'Enregistrer la séance'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function SeancesCoaching({ coaching, onRefresh }) {
  const [showModal, setShowModal] = useState(false);
  const [deleting, setDeleting]   = useState(null);

  const seances    = coaching.seances    || [];
  const nb_seances = coaching.nb_seances || { total: 0, realisees: 0, annulees: 0, reportees: 0 };

  const handleDelete = async (seanceId) => {
    if (!window.confirm('Supprimer cette séance ?')) return;
    setDeleting(seanceId);
    try {
      await api.delete(`/coachings/${coaching.id}/seances/${seanceId}/`);
      toast.success('Séance supprimée.');
      onRefresh();
    } catch {
      toast.error('Erreur lors de la suppression.');
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      {/* En-tête */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, paddingBottom: 12, borderBottom: '1px solid #E5E7EB' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <BookOpen size={15} color="#7C3AED" />
          <h3 style={{ fontSize: 14, fontWeight: 700, color: '#7C3AED', margin: 0 }}>
            Séances de coaching ({nb_seances.total})
          </h3>
        </div>
        <button
          className="header-btn btn-sm no-print"
          onClick={() => setShowModal(true)}
          style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, background: '#7C3AED', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 14px', cursor: 'pointer' }}>
          <Plus size={13} /> Enregistrer une séance
        </button>
      </div>

      {/* Compteurs rapides */}
      {nb_seances.total > 0 && (
        <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
          {[
            { label: 'Réalisées',  count: nb_seances.realisees, color: '#1E7145', bg: '#E8F5E9' },
            { label: 'Reportées',  count: nb_seances.reportees, color: '#ED7D31', bg: '#FFF3E0' },
            { label: 'Annulées',   count: nb_seances.annulees,  color: '#C00000', bg: '#FCE4EC' },
          ].map(c => (
            <div key={c.label} style={{ padding: '6px 14px', borderRadius: 20, background: c.bg, fontSize: 12, fontWeight: 700, color: c.color }}>
              {c.count} {c.label}
            </div>
          ))}
        </div>
      )}

      {/* Liste des séances */}
      {seances.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '24px 0', color: '#9CA3AF' }}>
          <BookOpen size={32} style={{ opacity: 0.3, marginBottom: 8 }} />
          <p style={{ fontSize: 13 }}>Aucune séance enregistrée pour ce coaching.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {seances.map((s, i) => {
            const st = STATUT_SEANCE_MAP[s.statut] || { label: s.statut, badge: 'gris', color: '#6B7280' };
            return (
              <div key={s.id} style={{
                border: '1px solid #E5E7EB', borderRadius: 10, overflow: 'hidden',
                borderLeft: `4px solid ${st.color}`,
              }}>
                {/* En-tête séance */}
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '10px 16px', background: '#F9FAFB', borderBottom: '1px solid #F3F4F6',
                  flexWrap: 'wrap', gap: 8,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#1F4E79' }}>
                      Séance #{seances.length - i}
                    </span>
                    <span className={`badge badge-${st.badge}`}>{st.label}</span>
                    <span style={{ fontSize: 12, color: '#6B7280' }}>
                      {fmt(s.date_seance)} · {s.duree_minutes} min
                    </span>
                  </div>
                  <button
                    className="no-print"
                    onClick={() => handleDelete(s.id)}
                    disabled={deleting === s.id}
                    title="Supprimer cette séance"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF', display: 'flex', alignItems: 'center', padding: 4 }}>
                    <Trash2 size={14} />
                  </button>
                </div>

                {/* Corps */}
                <div style={{ padding: '12px 16px' }}>
                  {s.theme && (
                    <div style={{ marginBottom: 8 }}>
                      <span style={{ fontSize: 11, color: '#6B7280', fontWeight: 600, textTransform: 'uppercase' }}>Thème</span>
                      <p style={{ fontSize: 13, fontWeight: 600, color: '#1F2937', margin: '2px 0 0' }}>{s.theme}</p>
                    </div>
                  )}
                  {s.compte_rendu && (
                    <div style={{ padding: '8px 12px', background: '#F5F3FF', borderRadius: 6, fontSize: 12, color: '#374151', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                      <span style={{ fontWeight: 600, color: '#7C3AED' }}>Compte rendu : </span>
                      {s.compte_rendu}
                    </div>
                  )}
                  {s.created_by_nom && (
                    <div style={{ marginTop: 8, fontSize: 11, color: '#9CA3AF' }}>
                      Enregistré par {s.created_by_nom}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showModal && (
        <ModalAjouterSeance
          coachingId={coaching.id}
          onClose={() => setShowModal(false)}
          onSuccess={() => { setShowModal(false); onRefresh(); }}
        />
      )}
    </div>
  );
}

/* ─────────────── Documents ─────────────── */
function DocumentsCoaching({ clientId, coachingId }) {
  const [docs,      setDocs]      = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [showForm,  setShowForm]  = useState(false);
  const [typeDoc,   setTypeDoc]   = useState('contrat');
  const [fichier,   setFichier]   = useState(null);
  const [uploading, setUploading] = useState(false);
  const inputId = `doc-upload-${coachingId}`;
  const inputRef = useRef(null);

  const charger = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/clients/documents/', { params: { client: clientId } });
      setDocs(data.results || data);
    } catch { setDocs([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (clientId) charger(); }, [clientId]); // eslint-disable-line

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!fichier) { toast.error('Sélectionnez un fichier.'); return; }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('client',        clientId);
      form.append('type_document', typeDoc);
      form.append('fichier',       fichier);
      await api.post('/clients/documents/', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Document téléversé avec succès.');
      setShowForm(false);
      setFichier(null);
      if (inputRef.current) inputRef.current.value = '';
      charger();
    } catch (err) {
      const msg = err.response?.data
        ? Object.values(err.response.data).flat().join(' | ')
        : 'Erreur lors du téléversement.';
      toast.error(msg);
    } finally { setUploading(false); }
  };

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid #E5E7EB' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <FileText size={15} color="#1F4E79" />
          <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1F4E79', margin: 0 }}>
            Documents du client ({docs.length})
          </h3>
        </div>
        <button
          className="header-btn btn-primary btn-sm"
          onClick={() => setShowForm(v => !v)}
          style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}
        >
          <Upload size={13} />
          {showForm ? 'Annuler' : 'Ajouter un document'}
        </button>
      </div>

      {/* Formulaire upload */}
      {showForm && (
        <div style={{ background: '#F0F7FF', border: '1px solid #BFDBFE', borderRadius: 8, padding: 16, marginBottom: 16 }}>
          <form onSubmit={handleUpload}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Type de document</label>
                <select className="form-control" value={typeDoc} onChange={e => setTypeDoc(e.target.value)}>
                  {Object.entries(TYPE_DOC_LABELS).map(([v, l]) => (
                    <option key={v} value={v}>{l}</option>
                  ))}
                </select>
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Fichier <span style={{ color: '#C00000' }}>*</span></label>
                {/* Input lié par htmlFor — ouvre le gestionnaire de fichiers */}
                <input
                  ref={inputRef}
                  id={inputId}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  style={{ position: 'absolute', width: 1, height: 1, opacity: 0, overflow: 'hidden', clip: 'rect(0,0,0,0)' }}
                  onChange={e => setFichier(e.target.files[0] || null)}
                />
                <label
                  htmlFor={inputId}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '8px 14px', border: '1px solid #D1D5DB',
                    borderRadius: 6, background: '#fff', cursor: 'pointer',
                    fontSize: 13, color: fichier ? '#1F2937' : '#6B7280',
                  }}
                >
                  <Paperclip size={14} color="#1F4E79" />
                  {fichier ? `${fichier.name} (${Math.round(fichier.size/1024)} Ko)` : 'Choisir depuis votre ordinateur…'}
                </label>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button type="button" className="header-btn btn-secondary btn-sm" onClick={() => setShowForm(false)}>
                Annuler
              </button>
              <button type="submit" className="header-btn btn-primary btn-sm" disabled={uploading}
                style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Upload size={13} />
                {uploading ? 'Envoi…' : 'Téléverser'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Liste */}
      {loading ? (
        <div className="spinner" style={{ padding: 20 }}><div className="spinner-ring" /> Chargement…</div>
      ) : docs.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '24px 0', color: '#9CA3AF' }}>
          <FileText size={32} style={{ opacity: 0.3, marginBottom: 8 }} />
          <p style={{ fontSize: 13 }}>Aucun document pour ce client.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {docs.map(d => (
            <div key={d.id} style={{
              display: 'flex', alignItems: 'center', gap: 12,
              padding: '10px 14px', borderRadius: 8, background: '#F9FAFB',
              border: '1px solid #E5E7EB',
            }}>
              <FileText size={16} color="#1F4E79" style={{ flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#1F2937' }}>
                    {TYPE_DOC_LABELS[d.type_document] || d.type_document}
                  </span>
                  <span className="badge badge-gris">v{d.version_doc}</span>
                </div>
                <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2 }}>
                  {d.nom_fichier} · {d.taille_ko} Ko · {new Date(d.date_upload).toLocaleDateString('fr-FR')}
                </div>
              </div>
              {d.url_fichier && (
                <a href={d.url_fichier} target="_blank" rel="noreferrer"
                  className="header-btn btn-secondary btn-sm btn-icon" title="Voir le fichier">
                  <Eye size={13} />
                </a>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

